import React, { useState, useEffect } from "react";
import {
  Layout, Table, Button, Space, Descriptions, Card, Tag, message, Row, Col, Tooltip, Modal, Timeline
} from "antd";
import { useParams, useNavigate } from "react-router-dom";
import Headerdiv from "../../layout/header";
import Footerdiv from "../../layout/footer";
import { AuditOutlined, PrinterOutlined, EditOutlined, DeleteOutlined, ClockCircleOutlined, CheckCircleOutlined, CloseCircleOutlined, EyeOutlined } from "@ant-design/icons";
import api from "../../../services/axiosInstance";
import { getAuthItem } from "../../../services/authService";
import moment from "moment";
import "../../../styles/fluidDesign.css";
import GRNPreviewModal from "../../../components/common/GRNPreviewModal";
import AppBreadcrumb from "../../../components/common/AppBreadcrumb";
import SupplierDetailsModal from "../../../components/common/SupplierDetailsModal";
import { getStatusColor } from "../../../utils/statusHelper";

const { Content } = Layout;

const ViewGRN = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [grnMaster, setGrnMaster] = useState(null);
  const [grnItems, setGrnItems] = useState([]);
  const [previewModalVisible, setPreviewModalVisible] = useState(false);
  const [supplierModalVisible, setSupplierModalVisible] = useState(false);

  const isDraft = grnMaster && grnMaster.grn_status === 'Draft';

  const getTimelineItems = () => {
    if (!grnMaster) return [];
    const rawItems = [];
    const status = grnMaster.grn_status;

    // Step 1: Created
    rawItems.push({
      statusName: 'GRN Created',
      statusLabel: 'Draft',
      color: '#2d5a3d',
      dotIcon: <ClockCircleOutlined style={{ color: '#2d5a3d' }} />,
      by: grnMaster.creator_name,
      date: grnMaster.grn_date ? moment(grnMaster.grn_date).format('YYYY-MM-DD hh:mm:ss A') : '',
      comment: null
    });

    // Step 2: Decision
    const reviewDateStr = grnMaster.approved_dt ? moment(grnMaster.approved_dt).format('YYYY-MM-DD hh:mm:ss A') : '';
    if (status === 'Approved' || status === 'Completed') {
      rawItems.push({
        statusName: 'GRN Approved',
        statusLabel: 'Approved',
        color: '#52c41a',
        dotIcon: <CheckCircleOutlined style={{ color: '#52c41a' }} />,
        by: grnMaster.approver_name,
        date: reviewDateStr,
        comment: grnMaster.approved_note
      });
    } else if (status === 'Rejected') {
      rawItems.push({
        statusName: 'GRN Rejected',
        statusLabel: 'Rejected',
        color: '#ff4d4f',
        dotIcon: <CloseCircleOutlined style={{ color: '#ff4d4f' }} />,
        by: grnMaster.approver_name,
        date: reviewDateStr,
        comment: grnMaster.approved_note
      });
    } else if (status === 'Pending' || status === 'Pending Approval') {
      rawItems.push({
        statusName: 'Awaiting Review',
        statusLabel: 'Pending',
        color: '#8c8c8c',
        dotIcon: <ClockCircleOutlined style={{ color: '#8c8c8c' }} />,
        by: null,
        date: '',
        comment: null
      });
    }

    const items = rawItems.map((item, idx) => {
      const stepNum = idx + 1;
      return {
        color: item.color,
        dot: (
          <div style={{
            padding: '4px',
            background: item.color === '#52c41a' ? '#f6ffed' : (item.color === '#ff4d4f' ? '#fff1f0' : '#f9f9f9'),
            borderRadius: '50%',
            border: `1px solid ${item.color}`
          }}>
            {item.dotIcon}
          </div>
        ),
        children: (
          <div style={{
            background: '#f9f9f9',
            padding: '12px 16px',
            borderRadius: '8px',
            borderLeft: `4px solid ${item.color}`
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
              <span style={{ fontWeight: 600, fontSize: '14px' }}>{item.statusName}</span>
              <Tag color={getStatusColor(item.statusLabel)}>Step {stepNum}</Tag>
            </div>
            {item.by && (
              <div style={{ fontSize: '13px', color: '#555' }}>
                By: <span style={{ fontWeight: 500 }}>{item.by}</span>
              </div>
            )}
            {item.date && (
              <div style={{ fontSize: '12px', color: '#888' }}>
                {item.date}
              </div>
            )}
            {item.comment && (
              <div style={{ marginTop: 8, padding: '8px', background: '#fff', borderRadius: '4px', border: '1px dashed #d9d9d9', fontSize: '12px', fontStyle: 'italic' }}>
                "{item.comment}"
              </div>
            )}
          </div>
        )
      };
    });

    return items.reverse();
  };

  const handleDeleteGRN = () => {
    Modal.confirm({
      title: 'Delete Goods Received Note',
      content: `Are you sure you want to delete ${grnMaster?.grn_ref}? This action cannot be undone.`,
      okText: 'Yes, Delete',
      okType: 'danger',
      cancelText: 'No',
      onOk: async () => {
        try {
          const res = await api.post("/inventory/grn/delete.php", { grn_id: id });
          if (res.data && res.data.status === "success") {
            message.success(res.data.message || "GRN deleted successfully.");
            navigate("/grn/list");
          } else {
            message.error(res.data.message || "Failed to delete GRN.");
          }
        } catch (error) {
          message.error("Error deleting GRN.");
        }
      }
    });
  };

  // Current system semantics: 'false' = enabled, 'true' = disabled, missing = denied
  const hasPermission = (permName) => {
    const value = getAuthItem(`USERPERMISSION_${permName}`);
    return value !== null && value !== 'true';
  };

  useEffect(() => {
    fetchGRNDetails();
  }, [id]);

  const fetchGRNDetails = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/inventory/grn/get.php?id=${id}`);
      if (res.data && res.data.status === "success") {
        setGrnMaster(res.data.master);
        setGrnItems(res.data.items);
      } else {
        message.error("Goods Received Note not found.");
      }
    } catch (error) {
      message.error("Failed to load Goods Received Note details.");
    } finally {
      setLoading(false);
    }
  };

  const renderStatusTag = (status) => {
    return <Tag color={getStatusColor(status)} style={{ fontWeight: "600" }}>{status}</Tag>;
  };

  return (
    <Layout className="fluid-page">
      <Headerdiv />

      <div className="fluid-page-header">
        <div className="floating-shape shape-1"></div>
        <div className="floating-shape shape-2"></div>
        <Row align="bottom" justify="space-between">
          <Col>
            <AppBreadcrumb items={[
              { label: 'Inventory', link: '/inventory-items' },
              { label: 'View GRN Details' },
            ]} />
            <h1 className="fluid-page-title">
              <AuditOutlined /> Goods Received Note: {grnMaster ? grnMaster.grn_ref : "Loading..."}
            </h1>
          </Col>
          <Col xs={0} md={12} style={{ textAlign: 'right' }}>
            <Space>
              {isDraft && hasPermission('grn_create') && (
                <Button
                  type="primary"
                  icon={<EditOutlined />}
                  onClick={() => navigate(`/grn/receive/${grnMaster.pr_id || 0}?grnId=${id}`)}
                  className="fluid-btn-visible"
                >
                  Edit
                </Button>
              )}
              {isDraft && hasPermission('grn_create') && (
                <Button
                  danger
                  icon={<DeleteOutlined />}
                  onClick={handleDeleteGRN}
                  style={{ background: "#fff", borderColor: "#ff4d4f", color: "#ff4d4f" }}
                >
                  Delete
                </Button>
              )}
              <Tooltip title="Print">
                <Button
                  icon={<PrinterOutlined />}
                  onClick={() => setPreviewModalVisible(true)}
                  className="fluid-btn-visible"
                />
              </Tooltip>
            </Space>
          </Col>
        </Row>
      </div>

      <Content className="fluid-content" style={{ minHeight: "calc(100vh - 350px)" }}>
        {grnMaster && (
          <Space direction="vertical" size="large" style={{ display: 'flex', width: '100%' }}>
            <Row gutter={[16, 16]}>
              <Col xs={24} lg={12}>
                <Card className="fluid-card" title="GRN Overview" style={{ height: '100%' }}>
                  <Descriptions bordered column={1} size="small">
                    <Descriptions.Item label="GRN Reference"><strong>{grnMaster.grn_ref}</strong></Descriptions.Item>
                    <Descriptions.Item label="GRN Create Date">{grnMaster.created_dt ? moment(grnMaster.created_dt).format("YYYY-MM-DD HH:mm") : (grnMaster.grn_date ? moment(grnMaster.grn_date).format("YYYY-MM-DD") : "-")}</Descriptions.Item>
                    <Descriptions.Item label="Created By">{grnMaster.creator_name || "-"}</Descriptions.Item>
                    <Descriptions.Item label="Status">{renderStatusTag(grnMaster.grn_status)}</Descriptions.Item>
                  </Descriptions>
                </Card>
              </Col>
              <Col xs={24} lg={12}>
                <Card className="fluid-card" title="PO / Supplier Details" style={{ height: '100%' }}>
                  <Descriptions bordered column={1} size="small">
                    <Descriptions.Item label="Supplier Ref">{grnMaster.sup_ref ? <Tag color="cyan">{grnMaster.sup_ref}</Tag> : "-"}</Descriptions.Item>
                    <Descriptions.Item label="Supplier Ref Date">{grnMaster.sup_ref_date ? moment(grnMaster.sup_ref_date).format("YYYY-MM-DD") : "-"}</Descriptions.Item>
                    <Descriptions.Item label="PO Associated">
                      {grnMaster.po_ref ? <Tag color="blue">{grnMaster.po_ref}</Tag> : "-"}
                    </Descriptions.Item>
                    <Descriptions.Item label="Approver">{grnMaster.approver_name || "-"}</Descriptions.Item>
                    <Descriptions.Item label="Approved Date">{grnMaster.approved_dt ? moment(grnMaster.approved_dt).format("YYYY-MM-DD HH:mm") : "-"}</Descriptions.Item>
                    <Descriptions.Item label="Approver Feedback">{grnMaster.approved_note || "No comments"}</Descriptions.Item>
                    {grnMaster.pr_note && (
                      <Descriptions.Item label="PR Note">{grnMaster.pr_note}</Descriptions.Item>
                    )}
                  </Descriptions>

                  {grnMaster.supplier_name && (
                    <div style={{ marginTop: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontWeight: 600, marginBottom: 4, color: 'var(--fluid-primary)' }}>Supplier</div>
                        <div style={{ fontSize: 15, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {grnMaster.supplier_name}
                        </div>
                      </div>
                      <Button icon={<EyeOutlined />} onClick={() => setSupplierModalVisible(true)}>
                        View Supplier
                      </Button>
                    </div>
                  )}
                </Card>
              </Col>
            </Row>

            <Card className="fluid-card" title="Delivered Line Items" loading={loading}>
              <Table
                dataSource={grnItems}
                rowKey="grnitem_id"
                pagination={false}
                bordered
                size="middle"
                columns={[
                  { title: "Item Code", dataIndex: "inv_code", key: "inv_code", render: (val) => val || "-" },
                  {
                    title: "Item Description",
                    key: "inv_desc",
                    render: (_, record) => (
                      <div>
                        <strong>{record.inv_desc}</strong>
                        {record.pr_item_note && (
                          <div style={{ fontSize: "12px", color: "#faad14", marginTop: 2, fontWeight: "500" }}>
                            PR Note: {record.pr_item_note}
                          </div>
                        )}
                        {record.grnitem_note && (
                          <div style={{ fontSize: "12px", color: "#1890ff", marginTop: 2, fontWeight: "500" }}>
                            GRN Note: {record.grnitem_note}
                          </div>
                        )}
                      </div>
                    )
                  },
                  { title: "Received Qty", dataIndex: "grn_qty", key: "grn_qty", align: "right", render: (qty) => <strong>{Number(qty).toFixed(2)}</strong> },
                  { title: "Unit Price", dataIndex: "cost_price", key: "cost_price", align: "right", render: (val) => Number(val).toFixed(2) },
                  { title: "Sub Total Cost", dataIndex: "item_subtotal", key: "item_subtotal", align: "right", render: (val) => <strong>LKR {Number(val).toLocaleString(undefined, { minimumFractionDigits: 2 })}</strong> }
                ]}
              />

              <div style={{ marginTop: 24, textAlign: "right" }}>
                <Descriptions column={1} size="small" bordered style={{ width: 340, display: "inline-block" }}>
                  <Descriptions.Item label="Sub Total">LKR {Number(grnMaster.grn_sub_total).toLocaleString(undefined, { minimumFractionDigits: 2 })}</Descriptions.Item>
                  <Descriptions.Item label="Tax">LKR {Number(grnMaster.grn_total_tax).toLocaleString(undefined, { minimumFractionDigits: 2 })}</Descriptions.Item>
                  <Descriptions.Item label="Discount">LKR {Number(grnMaster.grn_total_discount).toLocaleString(undefined, { minimumFractionDigits: 2 })}</Descriptions.Item>
                  <Descriptions.Item label="Grand Total">
                    <strong style={{ fontSize: "16px", color: "#52c41a" }}>LKR {Number(grnMaster.grn_final_amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}</strong>
                  </Descriptions.Item>
                </Descriptions>
              </div>
            </Card>

            <Card className="fluid-card" title={<><ClockCircleOutlined style={{ marginRight: 8 }} /> Approval History</>}>
              <div style={{ padding: '24px 12px' }}>
                <Timeline mode="left" items={getTimelineItems()} />
              </div>
            </Card>
          </Space>
        )}
      </Content>
      <GRNPreviewModal
        grnId={id}
        visible={previewModalVisible}
        onCancel={() => setPreviewModalVisible(false)}
      />
      <SupplierDetailsModal
        visible={supplierModalVisible}
        onCancel={() => setSupplierModalVisible(false)}
        supplier={grnMaster}
      />
      <Footerdiv />
    </Layout>
  );
};

export default ViewGRN;
